import {
  pushRecentGroupUuid,
  readRecentGroupUuids,
} from "@/lib/recent-group-uuids";
import { createContext, useContext, useState } from "react";

const USER_LOCAL_SETTINGS_LOCAL_STORAGE_KEY = "99r-user-local-settings";

type UserLocalSettings = {
  lastGroupUuid: string | undefined;
  recentGroupUuids: string[];
  chatWidth?: number;
  chatMinimized: boolean;
  modelProfileUuid: string | undefined;
};

const DEFAULT: UserLocalSettings = {
  lastGroupUuid: undefined,
  recentGroupUuids: [],
  chatWidth: 33,
  chatMinimized: false,
  modelProfileUuid: undefined,
};

const UserLocalSettingsContext = createContext<{
  settings: UserLocalSettings;
  setSettings: React.Dispatch<React.SetStateAction<UserLocalSettings>>;
}>({
  settings: DEFAULT,
  setSettings: () => {
    throw new Error(
      "setSettings() must be used within a DebugSettingsProvider",
    );
  },
});

export const UserLocalSettingsProvider = ({
  children,
}: React.PropsWithChildren) => {
  const [settings, setSettings] = useState(getLocalSettings);

  return (
    <UserLocalSettingsContext.Provider value={{ settings, setSettings }}>
      {children}
    </UserLocalSettingsContext.Provider>
  );
};

const getLocalSettings = (): UserLocalSettings => {
  const local = localStorage.getItem(USER_LOCAL_SETTINGS_LOCAL_STORAGE_KEY);
  const settings: Partial<UserLocalSettings> = JSON.parse(local || "{}");

  return {
    ...DEFAULT,
    ...settings,
    recentGroupUuids: readRecentGroupUuids(settings.recentGroupUuids),
  };
};

export const useUserLocalSettings = () => {
  const { settings, setSettings } = useContext(UserLocalSettingsContext);

  const updateSettings = (settings: Partial<UserLocalSettings>) => {
    const local = getLocalSettings();
    const newSettings = {
      ...local,
      ...settings,
    };
    localStorage.setItem(
      USER_LOCAL_SETTINGS_LOCAL_STORAGE_KEY,
      JSON.stringify(newSettings),
    );
    setSettings(newSettings);
  };

  return {
    lastGroupUuid: settings.lastGroupUuid,
    recentGroupUuids: settings.recentGroupUuids,
    setLastGroupUuid: (lastGroupUuid: string | undefined) => {
      const local = getLocalSettings();
      updateSettings({
        lastGroupUuid,
        recentGroupUuids: lastGroupUuid
          ? pushRecentGroupUuid(local.recentGroupUuids, lastGroupUuid)
          : local.recentGroupUuids,
      });
    },
    rememberRecentGroup: (groupUuid: string) => {
      const local = getLocalSettings();
      updateSettings({
        recentGroupUuids: pushRecentGroupUuid(
          local.recentGroupUuids,
          groupUuid,
        ),
      });
    },
    chatWidth: settings.chatWidth,
    setChatWidth: (chatWidth: number | undefined) => {
      updateSettings({
        chatWidth,
      });
    },
    chatMinimized: settings.chatMinimized,
    setChatMinimized: (chatMinimized: boolean) => {
      updateSettings({
        chatMinimized,
      });
    },
    modelProfileUuid: settings.modelProfileUuid,
    setModelProfileUuid: (modelProfileUuid: string | undefined) => {
      updateSettings({
        modelProfileUuid,
      });
    },
  };
};
